import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageSquare, X, Check, Mail, Globe, RotateCcw } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

type FilterStatus = 'open' | 'resolved' | 'all'

interface SupportMessage {
  id: string
  name: string
  email: string
  topic: string | null
  message: string
  locale: 'en' | 'ar'
  source: string
  status: 'open' | 'resolved'
  created_at: string
  resolved_at: string | null
  resolution_notes: string | null
  resolved_by_name: string | null
}

const TOPIC_LABELS: Record<string, string> = {
  account:  'Account',
  orders:   'Orders',
  delivery: 'Delivery',
  loyalty:  'Drops & Loyalty',
  games:    'Games & Prizes',
  ai:       'DrinX AI',
  payments: 'Payments',
}

export default function Support() {
  const [filter, setFilter] = useState<FilterStatus>('open')
  const [resolving, setResolving] = useState<SupportMessage | null>(null)
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<{ messages: SupportMessage[] }>({
    queryKey: ['admin-support', filter],
    queryFn: () => api.get(`/support?status=${filter}`).then(r => r.data),
    refetchInterval: 30_000,
  })

  const reopen = useMutation({
    mutationFn: (id: string) => api.patch(`/support/${id}/reopen`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-support'] })
      qc.invalidateQueries({ queryKey: ['support-open-count'] })
    },
  })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-white">Support Inbox</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Messages submitted via the drinx.jo contact form. Reply via email — replies go straight to the sender.
          </p>
        </div>
        <FilterTabs value={filter} onChange={setFilter} />
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="px-4 py-12 text-center text-gray-600 text-sm">Loading…</div>
        ) : !data?.messages.length ? (
          <div className="px-4 py-16 text-center text-gray-600 text-sm">
            {filter === 'open'
              ? '✓ Inbox is clear.'
              : 'No messages to show.'}
          </div>
        ) : (
          <div className="divide-y divide-[#222]">
            {data.messages.map(m => (
              <MessageRow
                key={m.id}
                message={m}
                onResolve={() => setResolving(m)}
                onReopen={() => reopen.mutate(m.id)}
              />
            ))}
          </div>
        )}
      </div>

      {resolving && (
        <ResolveModal
          message={resolving}
          onClose={() => setResolving(null)}
          onResolved={() => {
            qc.invalidateQueries({ queryKey: ['admin-support'] })
            qc.invalidateQueries({ queryKey: ['support-open-count'] })
            setResolving(null)
          }}
        />
      )}
    </div>
  )
}

function FilterTabs({ value, onChange }: { value: FilterStatus; onChange: (v: FilterStatus) => void }) {
  const tabs: { id: FilterStatus; label: string }[] = [
    { id: 'open',     label: 'Open' },
    { id: 'resolved', label: 'Resolved' },
    { id: 'all',      label: 'All' },
  ]
  return (
    <div className="inline-flex bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg p-0.5">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value === t.id ? 'bg-[#C41E3A] text-white' : 'text-gray-400 hover:text-white'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

function MessageRow({
  message: m,
  onResolve,
  onReopen,
}: {
  message: SupportMessage
  onResolve: () => void
  onReopen: () => void
}) {
  const isOpen = m.status === 'open'
  return (
    <div className="px-4 py-4">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="text-xs font-medium px-2.5 py-1 rounded-full border bg-blue-500/10 text-blue-400 border-blue-500/30">
          <MessageSquare size={10} className="inline mr-1 -mt-0.5" />
          {m.topic ? TOPIC_LABELS[m.topic] ?? m.topic : 'General'}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-400 inline-flex items-center gap-1">
          <Globe size={10} />
          {m.locale.toUpperCase()}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          isOpen ? 'bg-orange-500/15 text-orange-400' : 'bg-green-500/15 text-green-400'
        }`}>
          {isOpen ? 'Open' : 'Resolved'}
        </span>

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-gray-600">{formatDateTime(m.created_at)}</span>
          {isOpen ? (
            <button
              onClick={onResolve}
              className="flex items-center gap-1.5 text-xs bg-green-500/15 hover:bg-green-500/25 text-green-400 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Check size={13} /> Mark resolved
            </button>
          ) : (
            <button
              onClick={onReopen}
              className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 text-gray-400 px-3 py-1.5 rounded-lg transition-colors"
            >
              <RotateCcw size={13} /> Reopen
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white/[0.02] border border-[#222] rounded-lg p-3 md:col-span-1">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-1.5">From</p>
          <p className="text-sm text-white">{m.name}</p>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
            <Mail size={11} className="text-gray-600" />
            <a
              href={`mailto:${m.email}?subject=Re: Your DrinX support message`}
              className="text-blue-400 hover:underline"
            >{m.email}</a>
          </p>
        </div>
        <div className="bg-white/[0.02] border border-[#222] rounded-lg p-3 md:col-span-2">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-1.5">Message</p>
          <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">{m.message}</p>
        </div>
      </div>

      {!isOpen && (
        <div className="mt-3 px-3 py-2 bg-green-500/5 border border-green-500/20 rounded-lg">
          <p className="text-[10px] text-green-400 uppercase tracking-widest mb-1">
            Resolved {m.resolved_at && formatDateTime(m.resolved_at)}
            {m.resolved_by_name && ` by ${m.resolved_by_name}`}
          </p>
          {m.resolution_notes && (
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{m.resolution_notes}</p>
          )}
        </div>
      )}
    </div>
  )
}

function ResolveModal({
  message,
  onClose,
  onResolved,
}: {
  message: SupportMessage
  onClose: () => void
  onResolved: () => void
}) {
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => api.patch(`/support/${message.id}/resolve`, notes ? { notes } : {}),
    onSuccess: () => onResolved(),
    onError: (e: { response?: { data?: { message?: string } } }) =>
      setError(e.response?.data?.message ?? 'Failed to resolve'),
  })

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">Resolve message</h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="p-5">
          <p className="text-xs text-gray-500 mb-3">
            <span className="text-gray-300">{message.name}</span>{' · '}
            <span className="text-gray-500">{message.email}</span>
          </p>
          <label className="block text-xs text-gray-400 mb-1.5">Resolution notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="What did you do? (e.g. replied via email, escalated to operations)"
            rows={3}
            maxLength={1000}
            className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          />
          {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white"
            >Cancel</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {mutation.isPending ? 'Resolving…' : 'Mark resolved'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
